import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

import {
  ORBI_APPOINTMENT_ACTION,
  ORBI_APPOINTMENT_ACTION_LABEL,
  ORBI_APPOINTMENT_MAP_PREVIEW,
  ORBI_APPOINTMENT_SEE_ALL_LABEL,
  ORBI_CHECKED_IN_LABEL,
  ORBI_MAX_APPOINTMENT_CARDS,
  OrbiAppointmentAction,
} from '@patient-ui/shared/constants';
import { OrbiAppointment } from '@patient-ui/shared/models';
import {
  formatOrbiAppointmentDateTime,
  orbiAppointmentCardTitle,
  orbiAppointmentStateLabel,
  orbiHasMapPreview,
  orbiHasMappableDestination,
  orbiMapPreviewUrl,
  orbiMapUrl,
  resolveOrbiAppointmentActions,
} from '@patient-ui/shared/utils';
import { EnvironmentService } from '@patient-ui/shared-ui/utils';

/** An action the user activated on one appointment card (PCSA-13397 AC 7). */
export interface OrbiAppointmentCardNavigate {
  action: OrbiAppointmentAction;
  appointment: OrbiAppointment;
}

export type OrbiAppointmentActionVariant =
  | 'primary'
  | 'secondary'
  | 'alt-secondary'
  | 'link';

const ORBI_ACTION_VARIANT_LADDER: readonly OrbiAppointmentActionVariant[] = [
  'primary',
  'secondary',
  'alt-secondary',
  'link',
];

@Component({
  selector: 'patient-ui-orbi-appointment-card',
  templateUrl: './orbi-appointment-card.component.html',
  styleUrls: ['./orbi-appointment-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrbiAppointmentCardComponent {
  /** The mapped appointments to render (already normalized by the mapper). */
  @Input() appointments: OrbiAppointment[] = [];

  /** The reference instant for the state-dependent title. Defaults to now. */
  @Input() now: Date = new Date();

  /** Emitted when the user activates an action on one card. */
  @Output() navigate = new EventEmitter<OrbiAppointmentCardNavigate>();

  /** Emitted when the user asks to see their full appointment list. */
  @Output() seeAll = new EventEmitter<void>();

  /** Labels for the action buttons. */
  readonly actionLabel = ORBI_APPOINTMENT_ACTION_LABEL;

  readonly seeAllLabel = ORBI_APPOINTMENT_SEE_ALL_LABEL;

  /** Map preview geometry, mirrored into the `<img>` so it reserves its box. */
  readonly mapPreview = ORBI_APPOINTMENT_MAP_PREVIEW;

  constructor(private readonly environmentService: EnvironmentService) {}

  /** The appointments actually rendered — capped per AC 9. */
  get visibleAppointments(): OrbiAppointment[] {
    return this.appointments.slice(0, ORBI_MAX_APPOINTMENT_CARDS);
  }

  get hasSeeAll(): boolean {
    return this.visibleAppointments.length > 1;
  }

  /** True when exactly one appointment is present (single-card layout). */
  get isSingle(): boolean {
    return this.appointments.length === 1;
  }

  /** Past rows are split out upstream, so a set is always headed as upcoming. */
  get title(): string {
    if (this.isSingle) {
      return this.cardTitle(this.visibleAppointments[0]);
    }
    return 'Upcoming Appointments';
  }

  /** The state-dependent card title (AC 2). */
  cardTitle(appointment: OrbiAppointment): string {
    return orbiAppointmentCardTitle(appointment, this.now);
  }

  stateLabel(appointment: OrbiAppointment): string | null {
    return orbiAppointmentStateLabel(appointment, this.now);
  }

  /** The formatted date/time line, or '' when the payload carried neither. */
  dateTimeLine(appointment: OrbiAppointment): string {
    return formatOrbiAppointmentDateTime(appointment);
  }

  actionsFor(appointment: OrbiAppointment): OrbiAppointmentAction[] {
    return resolveOrbiAppointmentActions(appointment, this.now);
  }

  actionVariant(position: number): OrbiAppointmentActionVariant {
    const rung = position + (this.hasSeeAll ? 1 : 0);
    return ORBI_ACTION_VARIANT_LADDER[
      Math.min(rung, ORBI_ACTION_VARIANT_LADDER.length - 1)
    ];
  }

  isSpent(
    action: OrbiAppointmentAction,
    appointment: OrbiAppointment
  ): boolean {
    return (
      action === ORBI_APPOINTMENT_ACTION.checkIn && !!appointment.isCheckedIn
    );
  }

  /** One action's label — "Checked In" once check-in is done (PCSA-13577). */
  labelFor(
    action: OrbiAppointmentAction,
    appointment: OrbiAppointment
  ): string {
    return this.isSpent(action, appointment)
      ? ORBI_CHECKED_IN_LABEL
      : this.actionLabel[action];
  }

  trailingIconFor(
    action: OrbiAppointmentAction,
    appointment: OrbiAppointment
  ): string | undefined {
    if (action !== ORBI_APPOINTMENT_ACTION.checkIn) {
      return undefined;
    }
    return appointment.isCheckedIn ? 'check' : 'phone_iphone';
  }

  /** Google Maps link for the appointment address, or '' when unmappable. */
  mapUrl(appointment: OrbiAppointment): string {
    return orbiMapUrl(appointment);
  }

  /** True when the appointment has somewhere the user can actually be sent. */
  hasMappableDestination(appointment: OrbiAppointment): boolean {
    return orbiHasMappableDestination(appointment);
  }

  /** Google Static Maps image for the appointment location (AC 3.4). */
  mapPreviewUrl(appointment: OrbiAppointment): string {
    return orbiMapPreviewUrl(
      appointment,
      this.environmentService.googleMapsKey
    );
  }

  /** True when a map preview can be built for this appointment. */
  hasMapPreview(appointment: OrbiAppointment): boolean {
    return orbiHasMapPreview(
      appointment,
      this.environmentService.googleMapsKey
    );
  }

  onAction(action: OrbiAppointmentAction, appointment: OrbiAppointment): void {
    this.navigate.emit({ action, appointment });
  }

  onSeeAll(): void {
    this.seeAll.emit();
  }

  /**
   * trackBy for the card set. The confirmation number is the row's stable
   * identity; the index backs rows that arrived without one.
   */
  trackByConfirmation(index: number, appointment: OrbiAppointment): string {
    return appointment.confirmationNumber ?? `index-${index}`;
  }
}
